import type {
  Client,
  ExtendedClient,
  SupabaseClient,
  TablesUpdate,
} from "@repo/supabase/database-types"
import { logger } from "@repo/utils/logger"
import type { DataTableBaseSearch } from "~/lib/schemas/common"
import {
  createInPredefinedListFilter,
  createListFilter,
  DataTableQueryBuilder,
} from "~/lib/utils/datatable-query-builder"
import { QUERYABLE_CLIENT_STATUSES } from "~/modules/clients/clients.constants"
import type { CreateClientData, UpdateClientData } from "./clients.schema"

export const createClientsService = (supabase: SupabaseClient) => {
  return new ClientsService(supabase)
}

export type { ClientsService }

class ClientsService {
  log = logger.child({
    module: "clients-service",
  })

  constructor(private readonly supabase: SupabaseClient) {}

  async getManyExtended(filters?: DataTableBaseSearch) {
    const builder = new DataTableQueryBuilder(this.supabase, {
      table: "extended_clients",
      searchField: "fts_client",
      defaultSort: { field: "created_at", ascending: false },
      filters: {
        business_entity_unit_ids: createListFilter("business_entity_unit_id"),
        status: createInPredefinedListFilter(
          "status",
          QUERYABLE_CLIENT_STATUSES,
        ),
      },
    })

    const { data, count } = await builder.execute<ExtendedClient>({
      page: 1,
      page_size: 20,
      ...filters,
    })

    return {
      clients: data,
      count,
    }
  }

  async getOneExtended(clientSlug: string) {
    this.log.info("Getting single extended client", { clientSlug })

    const { data, error } = await this.supabase
      .from("extended_clients")
      .select("*")
      .eq("slug", clientSlug)
      .maybeSingle()

    if (error) {
      this.log.error("Error getting extended client", { error, clientSlug })
      throw error
    }

    if (!data) {
      this.log.warn("Client not found", { clientSlug })
      throw new Error("Client not found")
    }

    return data
  }

  async getMembers(clientSlug: string) {
    this.log.info("Getting client members", { clientSlug })

    // First get the client data including contact info
    const { data: client, error: clientError } = await this.supabase
      .from("extended_clients")
      .select("unit_id, contact_name, email")
      .eq("slug", clientSlug)
      .maybeSingle()

    if (clientError || !client) {
      this.log.error("Error getting client", { error: clientError, clientSlug })
      throw new Error("Client not found")
    }

    // Use the public get_unit_members function
    const { data: unitMembers, error: membersError } = await this.supabase.rpc(
      "get_unit_members",
      { p_unit_id: client.unit_id },
    )

    if (membersError) {
      this.log.error("Error getting unit members", {
        error: membersError,
        clientSlug,
      })
      throw membersError
    }

    return unitMembers
  }

  async getOverviewStats(clientSlug: string) {
    this.log.info("Getting client overview stats", { clientSlug })

    // First get the client data
    const { data: client, error: clientError } = await this.supabase
      .from("extended_clients")
      .select("id, unit_id")
      .eq("slug", clientSlug)
      .maybeSingle()

    if (clientError || !client) {
      this.log.error("Error getting client", { error: clientError, clientSlug })
      throw new Error("Client not found")
    }

    // Get member count using the existing getMembers method
    const members = await this.getMembers(clientSlug)
    const memberCount = members.length

    // Get project count for this client
    const { count: projectCount, error: projectError } = await this.supabase
      .from("projects")
      .select("*", { count: "exact", head: true })
      .eq("client_id", client.id)
      .neq("status", "archived")

    if (projectError) {
      this.log.error("Error getting project count", {
        error: projectError,
        clientSlug,
      })
      throw projectError
    }

    // Get unique resource count for this client (resources assigned to client's projects)
    const { data: resourceData, error: resourceError } = await this.supabase
      .from("project_resources")
      .select(`
        resource_id,
        projects!inner (
          client_id
        )
      `)
      .eq("projects.client_id", client.id)
      .eq("status", "active")

    if (resourceError) {
      this.log.error("Error getting resource count", {
        error: resourceError,
        clientSlug,
      })
      throw resourceError
    }

    // Count unique resources
    const uniqueResourceIds = new Set(
      resourceData?.map((pr) => pr.resource_id) || [],
    )
    const resourceCount = uniqueResourceIds.size

    return {
      memberCount,
      projectCount: projectCount || 0,
      resourceCount,
    }
  }

  async create(data: CreateClientData): Promise<Client> {
    this.log.info("Creating client", { name: data.name })

    // Get the business entity to get its unit_id
    const { data: businessEntity, error: businessEntityError } =
      await this.supabase
        .from("business_entities")
        .select("unit_id")
        .eq("id", data.businessEntityId)
        .eq("status", "active")
        .maybeSingle()

    if (businessEntityError || !businessEntity) {
      this.log.error("Error getting business entity", {
        error: businessEntityError,
      })
      throw new Error("Business entity not found or not active")
    }

    // Create address first if provided
    let billingAddressId: string | undefined
    if (data.address) {
      const { data: address, error: addressError } = await this.supabase
        .from("addresses")
        .insert({
          address_line_1: data.address.addressLine1,
          address_line_2: data.address.addressLine2,
          city: data.address.city,
          state: data.address.state,
          postal_code: data.address.postalCode,
          country: data.address.country,
          unit_id: businessEntity.unit_id,
        })
        .select("id")
        .single()

      if (addressError) {
        this.log.error("Error creating address", { error: addressError })
        throw new Error("Failed to create address")
      }

      billingAddressId = address.id
    }

    // Create the client (unit_id will be auto-generated by trigger)
    const { data: client, error: clientError } = await this.supabase
      .from("clients")
      .insert({
        name: data.name,
        vat_number: data.vatNumber,
        status: data.status,
        business_entity_id: data.businessEntityId,
        business_entity_unit_id: businessEntity.unit_id,
        contact_name: data.contactName,
        email: data.email,
        billing_address_id: billingAddressId,
        unit_id: crypto.randomUUID(), // Temporary, will be overridden by trigger
      })
      .select()
      .single()

    if (clientError) {
      this.log.error("Error creating client", { error: clientError })
      throw new Error("Failed to create client")
    }

    this.log.info("Client created successfully", { clientId: client.id })
    return client
  }

  async update(data: UpdateClientData): Promise<Client> {
    this.log.info("Updating client", { id: data.id, name: data.name })

    // First get the existing client to access current address and unit info
    const { data: existingClient, error: getError } = await this.supabase
      .from("clients")
      .select("billing_address_id, unit_id")
      .eq("id", data.id)
      .single()

    if (getError) {
      this.log.error("Error getting existing client", { error: getError })
      throw new Error("Client not found")
    }

    // Handle address update/creation
    let billingAddressId: string | undefined =
      existingClient.billing_address_id || undefined

    if (data.address) {
      if (existingClient.billing_address_id) {
        // Update existing address
        const { error: addressError } = await this.supabase
          .from("addresses")
          .update({
            address_line_1: data.address.addressLine1 || "",
            address_line_2: data.address.addressLine2 || "",
            city: data.address.city || "",
            state: data.address.state || "",
            postal_code: data.address.postalCode || "",
            country: data.address.country || "",
          })
          .eq("id", existingClient.billing_address_id)

        if (addressError) {
          this.log.error("Error updating address", { error: addressError })
          throw new Error("Failed to update address")
        }
      } else {
        // Create new address
        const { data: address, error: addressError } = await this.supabase
          .from("addresses")
          .insert({
            address_line_1: data.address.addressLine1 || "",
            address_line_2: data.address.addressLine2 || "",
            city: data.address.city || "",
            state: data.address.state || "",
            postal_code: data.address.postalCode || "",
            country: data.address.country || "",
            unit_id: existingClient.unit_id,
          })
          .select("id")
          .single()

        if (addressError) {
          this.log.error("Error creating address", { error: addressError })
          throw new Error("Failed to create address")
        }

        billingAddressId = address.id
      }
    } else if (existingClient.billing_address_id) {
      // Remove address if data.address is undefined/null
      const { error: deleteError } = await this.supabase
        .from("addresses")
        .delete()
        .eq("id", existingClient.billing_address_id)

      if (deleteError) {
        this.log.error("Error deleting address", { error: deleteError })
        // Don't throw here, just log the error and continue
      }

      billingAddressId = undefined
    }

    // Update the client
    // undefined = don't update the field (omit from update)
    // "" (empty string) = clear the field (set to null)
    const updateData: TablesUpdate<"clients"> = {
      name: data.name,
      status: data.status,
      billing_address_id: billingAddressId,
    }

    if (data.vatNumber !== undefined) {
      updateData.vat_number = data.vatNumber === "" ? null : data.vatNumber
    }
    if (data.contactName !== undefined) {
      updateData.contact_name =
        data.contactName === "" ? null : data.contactName
    }
    if (data.email !== undefined) {
      updateData.email = data.email === "" ? null : data.email
    }

    if (data.businessEntityId !== undefined) {
      // Get the business entity to get its unit_id
      const { data: businessEntity, error: businessEntityError } =
        await this.supabase
          .from("business_entities")
          .select("unit_id")
          .eq("id", data.businessEntityId)
          .eq("status", "active")
          .maybeSingle()

      if (businessEntityError) {
        this.log.error("Error getting business entity", {
          error: businessEntityError,
        })
      } else if (businessEntity) {
        updateData.business_entity_unit_id = businessEntity.unit_id
        updateData.business_entity_id = data.businessEntityId
      }
    }

    const { data: client, error: clientError } = await this.supabase
      .from("clients")
      .update(updateData)
      .eq("id", data.id)
      .select()
      .single()

    if (clientError) {
      this.log.error("Error updating client", { error: clientError })
      throw new Error("Failed to update client")
    }

    this.log.info("Client updated successfully", { clientId: client.id })
    return client
  }

  async archive({ clientId }: { clientId: string }): Promise<void> {
    const { error } = await this.supabase.rpc("soft_delete_clients", {
      p_id: clientId,
    })

    if (error) {
      this.log.error("Error archiving client", { error })
      throw new Error(error.message)
    }

    this.log.info("Client archived successfully", { clientId })
  }
}
